import type { ModelInfo } from '@paradigm/terminal-core';

import { defaultModelLabel } from './model-labels';

/**
 * Native offline fallback for the model picker, shown before (or when) the live
 * `/api/models` fetch resolves. Keyed by gateway host suffix because different
 * clusters may offer different catalogs.
 *
 * Ids carry the `dt-a/` openclaw provider prefix (anthropic-messages adapter)
 * followed by the `provider/company/model/version` convention. The fallback is
 * all `dt-a/` — the `dt-o/` adapter (openai-completions) only serves niche
 * OpenAI-compatible backends (OPENCLAW_OAI_ADAPTER_PREFIXES), none of which are
 * headline models. The live catalog replaces this list once it loads, so the
 * table only needs sensible defaults.
 */
interface StaticModel {
  readonly id: string;
  readonly legacy?: boolean;
}

const MODELS_BY_HOST_SUFFIX: Readonly<Record<string, readonly StaticModel[]>> =
  {
    // Production (dimeterminal.ai) and legacy (terminal.dime.gg) gateways.
    'dimeterminal.ai': [
      { id: 'dt-a/bedrock/anthropic/opus/5' },
      { id: 'dt-a/bedrock/anthropic/fable/5' },
      { id: 'dt-a/bedrock/anthropic/opus/4.8' },
      { id: 'dt-a/bedrock/anthropic/opus/4.7', legacy: true },
      { id: 'dt-a/bedrock/anthropic/sonnet/5' },
      { id: 'dt-a/bedrock/anthropic/sonnet/4.6' },
      { id: 'dt-a/bedrock/anthropic/sonnet/4.5' },
      { id: 'dt-a/bedrock/anthropic/haiku/4.5' },
      { id: 'dt-a/bedrock/deepseek/deepseek-v3.2' },
      { id: 'dt-a/bedrock/deepseek/deepseek-v3' },
      { id: 'dt-a/bedrock/google/gemma-3/27b' },
      { id: 'dt-a/bedrock/qwen/qwen-3/235b' },
      { id: 'dt-a/bedrock/qwen/qwen-3/coder-480b' },
      { id: 'dt-a/bedrock/openai/gpt-oss/120b' },
      { id: 'dt-a/bedrock/mistral/mistral-large-3/675b' },
      { id: 'dt-a/bedrock/z-ai/glm-5' },
      { id: 'dt-a/bedrock/z-ai/glm-4.7' },
      { id: 'dt-a/bedrock/moonshot-ai/kimi-k2.5' },
      { id: 'dt-a/qwen/qwen/qwen-3.7/max' },
      { id: 'dt-a/moonshot-ai/moonshot-ai/kimi-k3' },
      { id: 'dt-a/fireworks-ai/moonshot-ai/kimi-k3' },
      { id: 'dt-a/z-ai/z-ai/glm-5.2' },
      { id: 'dt-a/fireworks-ai/z-ai/glm-5.2' },
    ],
    'terminal.dime.gg': [
      { id: 'dt-a/bedrock/anthropic/opus/5' },
      { id: 'dt-a/bedrock/anthropic/fable/5' },
      { id: 'dt-a/bedrock/anthropic/opus/4.8' },
      { id: 'dt-a/bedrock/anthropic/opus/4.7', legacy: true },
      { id: 'dt-a/bedrock/anthropic/sonnet/5' },
      { id: 'dt-a/bedrock/anthropic/sonnet/4.6' },
      { id: 'dt-a/bedrock/anthropic/sonnet/4.5' },
      { id: 'dt-a/bedrock/anthropic/haiku/4.5' },
      { id: 'dt-a/bedrock/deepseek/deepseek-v3.2' },
      { id: 'dt-a/bedrock/google/gemma-3/27b' },
      { id: 'dt-a/bedrock/qwen/qwen-3/235b' },
      { id: 'dt-a/bedrock/openai/gpt-oss/120b' },
      { id: 'dt-a/bedrock/mistral/mistral-large-3/675b' },
      { id: 'dt-a/bedrock/z-ai/glm-5' },
    ],
  };

// Unknown gateways: a minimal Anthropic-only fallback that the live fetch
// corrects once it loads.
const DEFAULT_MODELS: readonly StaticModel[] = [
  { id: 'dt-a/bedrock/anthropic/opus/4.8' },
  { id: 'dt-a/bedrock/anthropic/sonnet/4.6' },
  { id: 'dt-a/bedrock/anthropic/haiku/4.5' },
];

function toModelInfo(m: StaticModel): ModelInfo {
  const label = defaultModelLabel(m.id);
  return m.legacy === true
    ? { id: m.id, label, legacy: true }
    : { id: m.id, label };
}

/**
 * Offline fallback list for the gateway at `agentUrl`. Returns `[]` for an
 * unparseable/empty URL so the picker simply stays hidden until the live fetch
 * lands, rather than guessing a wrong list.
 */
export function getStaticModels(agentUrl: string | null): readonly ModelInfo[] {
  if (agentUrl == null || agentUrl.length === 0) return [];
  let host: string;
  try {
    host = new URL(agentUrl).hostname;
  } catch {
    return [];
  }
  for (const [suffix, models] of Object.entries(MODELS_BY_HOST_SUFFIX)) {
    if (host === suffix || host.endsWith(`.${suffix}`)) {
      return models.map(toModelInfo);
    }
  }
  return DEFAULT_MODELS.map(toModelInfo);
}
